/* globals window requestAnimationFrame $ appSettings document */

import filterEvents from './events';
import multiselect from './multiselect';
import multiselectEvents from './multiselect/events';
import multicheckbox from './multicheckbox';
import multicheckboxEvents from './multicheckbox/events';
import userLocation from './userLocation';
import userLocationEvents from './userLocation/events';
import factory from '../../components/factory';
import toggleClass from '../../utils/toggleClass';
import matchBreakpoint from '../../utils/matchBreakpoint';
import trans from '../../utils/trans';

/**
 * Filters
 * @param {Element} element
 * @param {Object} settings
 * @return {Object}
 */
const filters = (element, settings) => {
  const { filterSet, $scrollContainer } = settings;
  const citySelect = element.querySelector('#citySelect');
  const categorySelect = element.querySelector('#categorySelect');
  const locationSelect = element.querySelector('#locationSelect');
  const digitalSelect = element.querySelector('#digitalSelect');
  const clearFilterElements = element.querySelectorAll('[data-js-filters-clear]');
  const selectedFiltersElement = element.querySelector('[data-js-filters-selectedFilters]');
  const filterBarWrapper = element.querySelector('.filters__filterBarWrapper');
  const filterBar = filterBarWrapper.querySelector('.filters__filterBar');
  const showFilters = filterBar.querySelector('[data-js-filters-showFilters]');
  const closeFilters = element.querySelector('[data-js-filters-close]');
  const applyFilters = element.querySelector('[data-js-filters-apply]');
  const timePickerElement = element.querySelector('[data-js-timepicker]');
  const favoriteClassesInput = element.querySelector('[data-js-favoriteClassesInput]');
  const startTimeInput = element.querySelector('[data-js-starttimeInput]');
  const queryInput = element.querySelector('[data-js-query]');

  const $element = $(element);
  const $mobileOverlay = $element.find('.filters__overlay:first-child');
  const $city = $(citySelect);
  const $categorySelect = $(categorySelect);
  const $locationSelect = $(locationSelect);
  const $digitalSelect = $(digitalSelect);
  const $categoryCheckbox = $('#categoryCheckbox');
  const $locationCheckbox = $('#locationCheckbox');
  const $userLocation = $('#userLocation');
  const $clearFilters = $(clearFilterElements);
  const $selectedFilters = $(selectedFiltersElement);
  const $showFilters = $(showFilters);
  const $filterBarWrapper = $(filterBarWrapper);
  const $filterBar = $(filterBar);
  const $closeFilters = $(closeFilters);
  const $applyFilters = $(applyFilters);
  const $favoriteClassesInput = $(favoriteClassesInput);
  const $startTimeInput = $(startTimeInput);
  const $timePickerElement = $(timePickerElement);
  const $queryInput = $(queryInput);

  let selectedCategories = [];
  let selectedLocations = [];
  let selectedUserLocation = {};
  let selectedDigital = 'BOTH';
  let startTime = '';
  let favoriteInput = false;
  let query = '';

  /**
   * Constructor
   */
  function constructor() {
    if (matchBreakpoint('xsmall')) {
      $city.select2({ closeOnSelect: true, minimumResultsForSearch: Infinity });
    }

    factory(multiselect, '[data-js-multiselect]');
    factory(multicheckbox, '[data-js-multicheckbox]');
    factory(userLocation, '#userLocation');

    bindEvents();
  }

  /**
   * Bind handlers to events
   */
  function bindEvents() {
    $element
      .on(filterEvents.SET_DATA, onSetData)
      .on(filterEvents.SHOW, onShow);

    $city.on('change', onCityChange);

    $categorySelect.on(multiselectEvents.CHANGE, onCategoryChange);
    $locationSelect.on(multiselectEvents.CHANGE, onLocationChange);

    $categoryCheckbox
      .on(multicheckboxEvents.CHANGE, onCategoryChange)
      .on(multicheckboxEvents.NUM_SELECTED_CHANGED, onNumSelectedChanged);

    $locationCheckbox
      .on(multicheckboxEvents.CHANGE, onLocationChange)
      .on(multicheckboxEvents.NUM_SELECTED_CHANGED, onNumSelectedChanged);

    $userLocation.on(userLocationEvents.CHANGE, onUserLocationChange);

    $clearFilters.on('click', onClearFilters);
    $selectedFilters.on('click', '[data-js-selected-filter]', onSelectedFilterClick);
    $showFilters.on('click', onShowFiltersClick);
    $closeFilters.on('click', onFiltersOverlayClose);
    $applyFilters.on('click', onApplyFilters);
    $scrollContainer.on('scroll', () => requestAnimationFrame(onScroll));

    $favoriteClassesInput.on('change', handleToggleFavoritesInput);
    $startTimeInput.on('change', handleTimeUpdate);
    $timePickerElement.on('change', handleTimePickerChange);
    $digitalSelect.on('change', handleDigitalUpdate);
    $queryInput.on('keyup keypress paste blur', handleQueryUpdate);

    $('[data-js-multicheckbox-trigger]').on('click', onMultiCheckboxTriggerClick);
  }

  /**
   * handle timepicker changing value
   */
  function handleTimePickerChange() {
    if (timePickerElement && timePickerElement.value) {
      startTime = timePickerElement.value;
      triggerFilterChange();
    }
  }

  /**
   * handle time input update for classesPage
   * @param {DOMEvent} e
   */
  function handleTimeUpdate(e) {
    startTime = e.target.value;
  }

  /**
   * Digital change event handler
   */
  function handleDigitalUpdate() {
    if (digitalSelect && digitalSelect.value) {
      selectedDigital = digitalSelect.value;
      triggerFilterChange();
    }
  }

  /**
   * Digital change event handler
   */
  function handleQueryUpdate() {
    if (queryInput) {
      query = queryInput.value;
      triggerFilterChange();
    }
  }

  /**
   * toggle favorite classes input
   * @param {Boolean} value
   */
  function handleToggleFavoritesInput(value) {
    favoriteInput = typeof value === 'boolean' ? value : !favoriteInput;
    if (favoriteInput) {
      favoriteClassesInput.setAttribute('checked', true);
    } else {
      favoriteClassesInput.removeAttribute('checked');
    }
    triggerFilterChange();
  }

  /**
   * Num selected changed event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onNumSelectedChanged(event, { numSelected }) {
    if (!matchBreakpoint('xsmall')) {
      const triggerId = event.target.getAttribute('id');
      const trigger = element.querySelector(`[data-js-multicheckbox-trigger="${triggerId}"]`);
      const placeholder = trigger.getAttribute('data-js-placeholder');
      const body = numSelected ? `${numSelected} ${trans('filter.selectedItems')}` : placeholder;

      trigger.innerText = body;
    }
  }

  /**
   * Trigger filter change
   * @param {Boolean} force
   */
  function triggerFilterChange(force) {
    if (matchBreakpoint('xsmall') || force) {
      const mapOptionId = option => option.id;
      const requestParams = {};

      if (filterSet.includes('category')) {
        requestParams.category_ids = selectedCategories.map(mapOptionId).join(',');
      }

      if (filterSet.includes('partner_ids')) {
        requestParams.partner_ids = selectedLocations.map(mapOptionId).join(',');
      }

      if (filterSet.includes('userLocation') && selectedUserLocation) {
        requestParams.zipcode = selectedUserLocation.zipcode;
        requestParams.distance = selectedUserLocation.distance;
        requestParams.latitude = selectedUserLocation.latitude;
        requestParams.longitude = selectedUserLocation.longitude;
      }

      if (filterSet.includes('workout_time')) {
        requestParams.workout_time = startTime;
      }

      if (filterSet.includes('get_favorite_schedules')) {
        requestParams.only_favorite_workouts = favoriteInput;
      }

      if (filterSet.includes('is_digital')) {
        if (selectedDigital === 'DIGITAL') {
          requestParams.is_digital = true;
        } else if (selectedDigital === 'LOCATION') {
          requestParams.is_digital = false;
        }
      }

      if (filterSet.includes('query')) {
        requestParams.query = query;
      }

      $element.trigger(filterEvents.CHANGE, requestParams);
    }

    updateSelectedFilters();
  }

  /**
   * Update selected filters list and count
   */
  function updateSelectedFilters() {
    const { hasZipcode, hasCoordinates } = selectedUserLocation;
    const numCategories = selectedCategories.length;
    const numLocations = selectedLocations.length;
    const numUserLocation = hasZipcode || hasCoordinates ? 1 : 0;
    const isFavoriteClassesEnabled = favoriteInput ? 1 : 0;
    const startTimeSpecified = startTime ? 1 : 0;
    const numDigital = selectedDigital !== 'BOTH' ? 1 : 0;
    const numQuery = query.length > 0 ? 1 : 0;
    let numSelectedFilters = numCategories + numLocations + numUserLocation + numDigital + numQuery;
    numSelectedFilters += isFavoriteClassesEnabled;
    numSelectedFilters += startTimeSpecified;
    const counters = [...element.querySelectorAll('[data-js-filter-count]')];

    counters.forEach((counter) => {
      counter.setAttribute('data-js-filter-count', numSelectedFilters);
      toggleClass(counter, '_has_count', !!numSelectedFilters);
    });

    const selectedFiltersHtml = selectedCategories
      .concat(selectedLocations)
      .reduce(renderSelectedFilterItem, '');

    selectedFiltersElement.innerHTML = selectedFiltersHtml;

    $element.trigger(filterEvents.NUM_SELECTED_FILTERS_CHANGED, { numSelectedFilters });
  }

  /**
   * Selected filter click event handler
   * @param {Event} event
   */
  function onSelectedFilterClick(event) {
    const selectedFilter = event.currentTarget;
    const id = selectedFilter.getAttribute('data-id');
    const $filter = $(selectedFilter.getAttribute('data-filter-selector'));

    $filter.trigger(multiselectEvents.CLEAR, { id });
  }

  /**
   * Clear filters event handler
   */
  function onClearFilters() {
    if (!hasFilterEnabled()) {
      return;
    }

    $categorySelect.trigger(multiselectEvents.CLEAR_ALL);
    $locationSelect.trigger(multiselectEvents.CLEAR_ALL);
    $categoryCheckbox.trigger(multicheckboxEvents.CLEAR_ALL);
    $locationCheckbox.trigger(multicheckboxEvents.CLEAR_ALL);
    $userLocation.trigger(userLocationEvents.CLEAR);
    $startTimeInput.val('06:00');
    $(timePickerElement).val('06:00');
    $queryInput.val('');
    $digitalSelect.val('BOTH');

    if (favoriteClassesInput) {
      handleToggleFavoritesInput(false);
    }

    selectedCategories.length = 0;
    selectedLocations.length = 0;
    selectedUserLocation = {};
    startTime = '';
    query = '';
    selectedDigital = 'BOTH';

    triggerFilterChange(true);

    if (!matchBreakpoint('xsmall')) {
      hide();
    }
  }

  /**
   * To check if any of the filters is selected/enabled
   */
  function hasFilterEnabled() {
    return (
      selectedCategories.length > 0 ||
      selectedLocations.length > 0 ||
      Object.keys(selectedUserLocation).length > 0 ||
      startTime.length > 0 ||
      favoriteInput === true ||
      selectedDigital !== 'BOTH' ||
      query.length > 0
    );
  }

  /**
   * City change event handler
   */
  function onCityChange() {
    window.location.href = `${window.location.origin}/${$city.val()}`;
  }

  /**
   * Category change event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onCategoryChange(event, { selectedOptions }) {
    selectedCategories = selectedOptions;
    triggerFilterChange();
  }

  /**
   * Location change event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onLocationChange(event, { selectedOptions }) {
    selectedLocations = selectedOptions;
    triggerFilterChange();
  }

  /**
   * User location change event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onUserLocationChange(event, eventData) {
    selectedUserLocation = eventData;
    triggerFilterChange();
  }

  /**
   * Set data event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onSetData(event, { locations, categories }) {
    $categorySelect.trigger(multiselectEvents.SET_DATA, { options: categories });
    $locationSelect.trigger(multiselectEvents.SET_DATA, { options: locations });
    $categoryCheckbox.trigger(multicheckboxEvents.SET_DATA, { options: categories });
    $locationCheckbox.trigger(multicheckboxEvents.SET_DATA, { options: locations });
  }

  /**
   * Show event handler
   */
  function onShow() {
    $element.addClass('_is_visible');
    $mobileOverlay.addClass('_is_visible');
  }

  /**
   * Hide the overlay
   */
  function hide() {
    $mobileOverlay.removeClass('_is_visible');
    $element.trigger(filterEvents.HIDE);
  }

  /**
   * Close click event handler
   */
  function onCloseClick() {
    hide();
  }

  /**
   * Close click event handler
   */
  function onFiltersOverlayClose() {
    $element.removeClass('_is_visible');
  }

  /**
   * Apply filters event handler
   * @param {Event} event
   */
  function onApplyFilters(event) {
    event.preventDefault();
    triggerFilterChange(true);
    onCloseClick();
    onFiltersOverlayClose();
  }

  /**
   * Show filters event handler
   */
  function onShowFiltersClick() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  /**
   * Scroll event handler
   */
  function onScroll() {
    const offsetTop = $filterBarWrapper.offset().top;
    const windowScroll = $(document).scrollTop();
    const isSticky = windowScroll - (offsetTop + appSettings.topNavigationHeight) >= 0;

    $filterBar.toggleClass('_is_sticky', isSticky);
  }

  /**
   * Multicheckbox trigger click event handler
   * @param {Event} event
   */
  function onMultiCheckboxTriggerClick(event) {
    const checkboxSelector = event.currentTarget.getAttribute('data-js-multicheckbox-trigger');
    element.querySelector(`#${checkboxSelector}`).classList.add('_is_visible');
  }

  /**
   * Returns rendered selected filter item HTML
   * @param {String} acc
   * @param {Object} selectedFilter
   * @return {String}
   */
  function renderSelectedFilterItem(acc, { id, name, selector }) {
    return `${acc}
      <li class="filters__selectedFilter"
        data-id="${id}"
        data-filter-selector="${selector}"
        data-js-selected-filter>
        <span class="filters__selectedFilter__text"> ${name} </span>
        <i class="icon icon_close selectedFilter__closeIcon"></i>
      </li>
    `;
  }

  constructor();

  return {};
};

export default filters;



// WEBPACK FOOTER //
// ./resources/assets/js/specifics/filters/filters.js